import * as Yup from "yup";

export type DenimUploadBatchStatus =
  | "DRAFT"
  | "QUEUED"
  | "RUNNING"
  | "STOP_REQUESTED"
  | "STOPPED"
  | "COMPLETED"
  | "COMPLETED_WITH_ERRORS"
  | "FAILED";

export type DenimUploadBatchItemStatus =
  | "PENDING"
  | "PROCESSING"
  | "COMPLETED"
  | "SKIPPED"
  | "FAILED"
  | "INVALID"
  | "DUPLICATE_IN_FILE";

export type FactoringAttemptStep = "JOB_CREATION" | "DOCUMENT_UPLOAD";

export type FactoringAttemptOutcome =
  | "IN_PROGRESS"
  | "SUCCESS"
  | "SKIPPED_ALREADY_SENT"
  | "SKIPPED_NO_DOCUMENTS"
  | "DENIM_DUPLICATE"
  | "FAILED";

export type FactoringAttemptDocumentOutcome = "SENT" | "SKIPPED_ALREADY_SENT" | "FAILED";

export type FactoredInvoiceStatus = "PENDING" | "JOB_CREATED" | "DOCUMENTS_SENT" | "NEEDS_ATTENTION";

export type FactoredInvoiceDocumentStatus = "NOT_SENT" | "SENT" | "FAILED";

export type FactoringDocumentType = "INVOICE" | "BILL_OF_LADING" | "PROOF_OF_DELIVERY" | "WEIGHT_AND_INSPECTION";

export type FactoringNotificationEvent =
  | "BATCH_STARTED"
  | "BATCH_COMPLETED"
  | "BATCH_COMPLETED_WITH_ERRORS"
  | "BATCH_STOPPED"
  | "BATCH_FAILED";

export const FACTORING_NOTIFICATION_EVENTS: FactoringNotificationEvent[] = [
  "BATCH_STARTED",
  "BATCH_COMPLETED",
  "BATCH_COMPLETED_WITH_ERRORS",
  "BATCH_STOPPED",
  "BATCH_FAILED",
];

export type ActivityEntityType = "BATCH" | "BATCH_ITEM" | "FACTORED_INVOICE" | "NOTIFICATION_SETTINGS";

export interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  next: number | null;
}

export interface PagedResponse<T> {
  items: T[];
  pagination: Pagination;
}

export interface ListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
}

export interface ListBatchesQuery extends ListQuery {
  status?: DenimUploadBatchStatus;
}

export interface ListBatchItemsQuery extends ListQuery {
  status?: DenimUploadBatchItemStatus;
}

/** Same definitions as the overview's "Needs attention" and "Document gaps" metrics. */
export type InvoiceListView = "needsAttention" | "documentGaps";

export interface ListInvoicesQuery extends ListQuery {
  status?: FactoredInvoiceStatus;
  view?: InvoiceListView;
  /** Inclusive ISO start of the first tracked time. */
  trackedFrom?: string;
  /** Exclusive ISO end of the first tracked time. */
  trackedTo?: string;
}

export interface DenimUploadBatch {
  id: string;
  batchNumber: number;
  name: string;
  fileName: string;
  checksum: string;
  status: DenimUploadBatchStatus;
  totalCount: number;
  processedCount: number;
  succeededCount: number;
  skippedCount: number;
  failedCount: number;
  invalidCount: number;
  leaseOwner: string | null;
  leaseExpiresAt: string | null;
  lastError: string | null;
  startedAt: string | null;
  startedBy: string | null;
  stoppedAt: string | null;
  stoppedBy: string | null;
  completedAt: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface DenimUploadBatchDetail extends DenimUploadBatch {
  itemsByStatus: Record<DenimUploadBatchItemStatus, number>;
}

export interface FactoringStats {
  byStatus: Record<DenimUploadBatchStatus, number>;
  active: number;
  totalBatches: number;
  trackedInvoices: number;
  invoicesNeedingAttention: number;
}

export type AnalyticsGranularity = "day" | "week" | "month";

export type FactoringIssueType = "JOB_FAILED" | "DENIM_DUPLICATE" | "DOCUMENTS_FAILED" | "NO_PROOF_OF_DELIVERY";

export type DocumentGapBucket = "UNDER_1_DAY" | "1_TO_3_DAYS" | "3_TO_7_DAYS" | "OVER_7_DAYS";

export interface AnalyticsOverviewQuery {
  from?: string;
  to?: string;
  granularity?: AnalyticsGranularity;
}

export interface MetricRate {
  numerator: number;
  denominator: number;
}

export interface AnalyticsTrendPoint {
  period: string;
  tracked: number;
  trackedValueCents: number;
  submitted: number;
  submittedValueCents: number;
}

export interface PayorSummary {
  payorNumber: number | null;
  payorName: string | null;
  trackedInvoices: number;
  trackedValueCents: number;
  submissionComplete: MetricRate;
  needsAttention: number;
  documentGaps: number;
  p50Seconds: number | null;
}

/** Per-period values aligned with `AnalyticsOverview.trend`. Key "OTHER" groups every payor outside the series. */
export interface PayorTrendSeries {
  key: string;
  payorNumber: number | null;
  payorName: string | null;
  tracked: number[];
  valueCents: number[];
}

export interface AnalyticsOverview {
  range: { from: string; to: string; granularity: AnalyticsGranularity };
  generatedAt: string;
  pipeline: {
    trackedInvoices: number;
    trackedValueCents: number;
    byStatus: { status: FactoredInvoiceStatus; count: number; valueCents: number }[];
    submissionComplete: MetricRate;
  };
  batchMonitor: {
    batches: number;
    batchesByStatus: Partial<Record<DenimUploadBatchStatus, number>>;
    rows: {
      total: number;
      valid: number;
      invalid: number;
      duplicateInFile: number;
      waiting: number;
      processing: number;
      completed: number;
      skipped: number;
      failed: number;
    };
  };
  issues: {
    total: number;
    valueCents: number;
    byType: { type: FactoringIssueType; count: number; valueCents: number }[];
  };
  trend: AnalyticsTrendPoint[];
  payors: {
    payorCount: number;
    top: PayorSummary[];
    series: PayorTrendSeries[];
  };
  processingTime: { sampleSize: number; p50Seconds: number | null; p95Seconds: number | null };
  documentGaps: {
    total: number;
    valueCents: number;
    buckets: { bucket: DocumentGapBucket; count: number; valueCents: number }[];
  };
  retryHealth: {
    firstAttemptSuccess: MetricRate;
    retriedInvoices: MetricRate;
    retriedRecovered: MetricRate;
    unresolvedFailures: MetricRate;
    failedAttempts: MetricRate;
  };
  duplicatePrevention: {
    duplicateRowsInFiles: MetricRate;
    previouslySentSkipped: MetricRate;
    multipleBatches: MetricRate;
  };
}

export interface CreateBatchRequest {
  file: File;
  name?: string;
  start: boolean;
}

export interface CreateBatchResponse {
  batch: DenimUploadBatch;
  warnings: string[];
}

export interface AttemptDocumentRef {
  id: string;
  sourceDocumentId: string;
  fileName: string | null;
  status?: FactoredInvoiceDocumentStatus;
  sentAt?: string | null;
}

export interface AttemptDocument {
  outcome: FactoringAttemptDocumentOutcome;
  document: AttemptDocumentRef;
}

export interface AttemptSummary {
  id: string;
  step: FactoringAttemptStep;
  outcome: FactoringAttemptOutcome;
  responseStatus: number | null;
  errorMessage: string | null;
  mergedFileName: string | null;
  retryCount: number;
  startedAt: string;
  finishedAt: string | null;
  triggeredBy: string;
  documents: AttemptDocument[];
}

export interface DenimUploadBatchItem {
  id: string;
  batchId: string;
  rowNumber: number;
  rawValue: string | null;
  probillNumber: string | null;
  factoredInvoiceId: string | null;
  status: DenimUploadBatchItemStatus;
  jobStepOutcome: FactoringAttemptOutcome | null;
  documentStepOutcome: FactoringAttemptOutcome | null;
  errorMessage: string | null;
  attemptCount: number;
  processedAt: string | null;
  createdAt: string;
  updatedAt: string | null;
  factoredInvoice: {
    id: string;
    probillNumber: string;
    denimJobId: string | null;
    status: FactoredInvoiceStatus;
    statusReason: string | null;
    payorName: string | null;
    invoiceAmountCents: number | null;
  } | null;
  attempts: AttemptSummary[];
}

export interface ActivityLogEntry {
  id: string;
  entityType: ActivityEntityType;
  entityId: string;
  action: string;
  message: string;
  metadata: Record<string, any> | null;
  actorEmail: string;
  createdAt: string;
}

export interface FactoredInvoiceBase {
  id: string;
  probillNumber: string;
  bolNumber: string | null;
  billDate: string | null;
  payorNumber: number | null;
  payorName: string | null;
  invoiceAmountCents: number | null;
  denimJobId: string | null;
  denimJobStatus: string | null;
  denimJobStatusSyncedAt: string | null;
  jobCreatedAt: string | null;
  jobCreatedByAttemptId: string | null;
  status: FactoredInvoiceStatus;
  statusReason: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface FactoredInvoiceListItem extends FactoredInvoiceBase {
  attemptCount: number;
  documentCount: number;
  documentsSentCount: number;
}

export interface FactoredInvoiceDocument {
  id: string;
  factoredInvoiceId: string;
  sourceDocumentId: string;
  type: FactoringDocumentType;
  fileName: string | null;
  sourceCreatedAt: string | null;
  status: FactoredInvoiceDocumentStatus;
  sentAt: string | null;
  sentByAttemptId: string | null;
  createdAt: string;
  createdBy: string;
  attemptDocuments: { attemptId: string; outcome: FactoringAttemptDocumentOutcome; createdAt: string }[];
}

export interface FactoredInvoiceAttempt extends Omit<AttemptSummary, "documents"> {
  factoredInvoiceId: string;
  batchId: string | null;
  batchItemId: string | null;
  requestPayload: any;
  responseBody: any;
  batch: { id: string; batchNumber: number; name: string } | null;
  documents: { outcome: FactoringAttemptDocumentOutcome; document: AttemptDocumentRef }[];
}

export interface FactoredInvoiceDetail extends FactoredInvoiceBase {
  isProcessing: boolean;
  documents: FactoredInvoiceDocument[];
  attempts: FactoredInvoiceAttempt[];
  batchItems: {
    id: string;
    rowNumber: number;
    status: DenimUploadBatchItemStatus;
    errorMessage: string | null;
    processedAt: string | null;
    batch: { id: string; batchNumber: number; name: string; status: DenimUploadBatchStatus };
  }[];
  activity: ActivityLogEntry[];
}

export interface NotificationRecipient {
  id: string;
  email: string;
  events: FactoringNotificationEvent[];
  active: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface NotificationSettings {
  recipients: NotificationRecipient[];
  activity: ActivityLogEntry[];
}

export interface NotificationRecipientRequest {
  email: string;
  events: FactoringNotificationEvent[];
  active: boolean;
}

export const NotificationRecipientSchema = Yup.object({
  email: Yup.string().trim().email("Enter a valid email").required("Email is required"),
  events: Yup.array()
    .of(Yup.string().oneOf(FACTORING_NOTIFICATION_EVENTS))
    .min(1, "Select at least one notification")
    .required("Select at least one notification"),
  active: Yup.boolean().required(),
}).defined();
